import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { GradePill, RATING_STYLES } from "../teacher/progress";

export function AcademicOverviewTab() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/progress/overview").then((r) => setD(r.data)).catch(() => setD(null)); }, []);
  if (!d) return <Card className="p-10 rounded-2xl border-dashed border-slate-300 text-center text-slate-500 text-sm">Loading academic report…</Card>;

  const cards = [
    ["Students tracked", d.students_tracked, null],
    ["Avg attendance", d.average_attendance, "%"],
    ["Avg exam score", d.average_exam_percentage, "%"],
    ["Homework completion", d.average_homework_completion, "%"],
    ["Avg homework grade", d.average_homework_grade, "%"],
    ["Overall progress", d.average_overall_progress, "%"],
  ];

  return (
    <div className="space-y-6" data-testid="manager-academic-overview">
      <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
        {cards.map(([k, v, suf]) => (
          <Card key={k} className="p-5 rounded-2xl border-slate-200">
            <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-2 font-serif text-2xl font-black text-[#0F1E4F]">{v == null ? "—" : `${v}${suf || ""}`}</div>
            {suf === "%" && <Progress value={v || 0} className="h-1.5 mt-2" />}
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[["Exams", d.exams_scheduled], ["Results recorded", d.exam_results_recorded], ["Homework assigned", d.homework_assigned], ["Progress reports", d.progress_reports]].map(([k, v]) => (
          <Card key={k} className="p-5 rounded-2xl border-slate-200">
            <div className="text-xs uppercase tracking-widest text-slate-500">{k}</div>
            <div className="mt-2 font-serif text-2xl font-black text-[#0F1E4F]">{v}</div>
          </Card>
        ))}
      </div>

      <Card className="rounded-2xl border-slate-200 overflow-hidden">
        <div className="px-5 pt-5 font-serif font-black text-[#0F1E4F]">Students needing academic attention</div>
        {d.students_needing_attention.length === 0
          ? <div className="p-8 text-center text-slate-500 text-sm">No students currently flagged.</div> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>Attendance</TableHead>
                <TableHead>Exam avg</TableHead><TableHead>HW completion</TableHead><TableHead>Overall</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {d.students_needing_attention.map((s) => (
                  <TableRow key={s.student_id} data-testid={`attention-${s.student_id}`}>
                    <TableCell className="font-semibold text-[#0F1E4F]">{s.student_name}</TableCell>
                    <TableCell>{s.class_name || "—"}</TableCell>
                    <TableCell>{s.attendance_percentage == null ? "—" : `${s.attendance_percentage}%`}</TableCell>
                    <TableCell>{s.exam_average_percentage == null ? "—" : `${s.exam_average_percentage}%`}</TableCell>
                    <TableCell>{s.homework_completion_percentage == null ? "—" : `${s.homework_completion_percentage}%`}</TableCell>
                    <TableCell><Badge className="rounded-full border-0 bg-red-100 text-red-800">{s.overall_progress == null ? "—" : `${s.overall_progress}%`}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </Card>

      <Card className="rounded-2xl border-slate-200 overflow-x-auto">
        <div className="px-5 pt-5 font-serif font-black text-[#0F1E4F]">All student progress</div>
        {d.summaries.length === 0 ? <div className="p-8 text-center text-slate-500 text-sm">No students enrolled in classes yet.</div> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Student</TableHead><TableHead>Class</TableHead><TableHead>Book</TableHead>
              <TableHead>Attendance</TableHead><TableHead>Exam avg</TableHead><TableHead>HW done</TableHead>
              <TableHead>Overall</TableHead><TableHead>Grade</TableHead><TableHead>Rating</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {d.summaries.map((s) => (
                <TableRow key={s.student_id} data-testid={`manager-progress-${s.student_id}`}>
                  <TableCell className="font-semibold text-[#0F1E4F]">{s.student_name}</TableCell>
                  <TableCell>{s.class_name || "—"}</TableCell>
                  <TableCell>{s.current_course || "—"}</TableCell>
                  <TableCell>{s.attendance_percentage == null ? "—" : `${s.attendance_percentage}%`}</TableCell>
                  <TableCell>{s.exam_average_percentage == null ? "—" : `${s.exam_average_percentage}%`}</TableCell>
                  <TableCell>{s.homework_completion_percentage == null ? "—" : `${s.homework_completion_percentage}%`}</TableCell>
                  <TableCell className="font-semibold">{s.overall_progress == null ? "—" : `${s.overall_progress}%`}</TableCell>
                  <TableCell><GradePill letter={s.overall_letter_grade} /></TableCell>
                  <TableCell>{s.progress_report_rating ? <Badge className={`rounded-full border-0 ${RATING_STYLES[s.progress_report_rating]}`}>{s.progress_report_rating}</Badge> : "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
}

export function ManagerLessonsTab() {
  const [lessons, setLessons] = useState([]);
  const [classes, setClasses] = useState([]);
  useEffect(() => {
    api.get("/lessons").then((r) => setLessons(r.data)).catch(() => setLessons([]));
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => setClasses([]));
  }, []);
  return (
    <Card className="rounded-2xl border-slate-200 overflow-hidden">
      {lessons.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">No lessons recorded yet.</div> : (
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Class</TableHead><TableHead>Title</TableHead><TableHead>Topics</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {lessons.map((l) => (
              <TableRow key={l.id} data-testid={`manager-lesson-${l.id}`}>
                <TableCell>{l.date}</TableCell>
                <TableCell>{classes.find((c) => c.id === l.class_id)?.name || "—"}</TableCell>
                <TableCell className="font-semibold text-[#0F1E4F]">{l.title}</TableCell>
                <TableCell className="max-w-sm truncate">{l.topics || l.description || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Card>
  );
}
